import { ChangeEvent, forwardRef, Ref, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import { useMergeRefs } from '@xemplo/ref-utils';

import { checkmarkSizeMap } from '../checkbox.constants';
import { CheckboxSize } from '../checkbox.types';

import { CheckboxV2Props } from './checkbox-v2.types';

import styles from './checkbox-v2.module.css';

export const CheckboxV2TestId = {
  input: (id?: string | number) => `checkbox-input-${id}`,
  label: (id?: string | number) => `checkbox-label-${id}`,
  span: 'checkbox-span',
};

type MarkProps = Readonly<{
  size: CheckboxSize;
  checked: boolean;
  indeterminate: boolean;
}>;

function Mark({ size, checked, indeterminate }: MarkProps) {
  if (indeterminate) return <span className={styles.dash} />;
  if (!checked) return null;
  return checkmarkSizeMap[size];
}

function CheckboxInternal(props: CheckboxV2Props, ref?: Ref<HTMLInputElement>) {
  const {
    ariaLabel,
    id,
    name,
    checked,
    disabled = false,
    indeterminate = false,
    size = CheckboxSize.Large,
    value,
    onChange,
    onBlur,
    invalid = false,
    className,
  } = props;

  const localInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useMergeRefs([localInputRef, ref]);

  // The browser clears `indeterminate` on click, so it's re-applied after every render and change.
  useLayoutEffect(() => {
    if (localInputRef.current) localInputRef.current.indeterminate = indeterminate;
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    event.currentTarget.indeterminate = indeterminate;
  };

  return (
    <label
      className={classNames(styles.label, className)}
      data-size={size}
      data-testid={CheckboxV2TestId.label(id ?? name)}
      data-role="checkbox"
    >
      <input
        className={styles.input}
        type="checkbox"
        id={id}
        name={name}
        ref={inputRef}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        onBlur={onBlur}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        data-testid={CheckboxV2TestId.input(id ?? name)}
      />
      <span
        className={styles.box}
        data-testid={CheckboxV2TestId.span}
        data-checked={checked || indeterminate}
        data-invalid={invalid}
        data-role="checkbox"
      >
        <Mark size={size} checked={checked} indeterminate={indeterminate} />
      </span>
    </label>
  );
}

export const CheckboxV2 = forwardRef(CheckboxInternal);
export default CheckboxV2;
